import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setDarkMode } from '../../store/features/uiSlice';

/**
 * Theme Provider Component
 * Handles applying dark mode class to the document and persisting theme preference
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isDarkMode = useAppSelector((state) => state.ui.isDarkMode);

  // Apply dark mode class to document and persist preference
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Persist theme preference to localStorage
    localStorage.setItem('traveller-ship-builder-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Initialize theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('traveller-ship-builder-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // Use saved theme, or default to system preference, or default to light
    const shouldUseDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    if (shouldUseDark !== isDarkMode) {
      dispatch(setDarkMode(shouldUseDark));
    }
  }, [dispatch, isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only apply system preference if no saved preference exists
      const savedTheme = localStorage.getItem('traveller-ship-builder-theme');
      if (!savedTheme && e.matches !== isDarkMode) {
        dispatch(setDarkMode(e.matches));
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [dispatch, isDarkMode]);

  return <>{children}</>;
};
